"use client";

import { useEffect } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

/**
 * SmoothLoadingScreen — controls the inline-script loading overlay.
 *
 * The actual overlay DOM element (`#__loading-screen`) is created by an inline
 * <script> in layout.tsx that runs before React hydrates. It reads localStorage
 * for cached branding, so white-label users instantly see their own branding
 * (never InsuraCentral). InsuraCentral users see the default favicon + blue spinner.
 *
 * This component only handles the fade-out + removal once auth finishes.
 */
export function SmoothLoadingScreen() {
  const { isInitializing } = useUserContext();

  useEffect(() => {
    const el = document.getElementById("__loading-screen");
    if (!el) return;

    if (isInitializing) {
      document.body.classList.add("loading");
      return;
    }

    // Auth finished — tear the overlay down IMMEDIATELY.
    //
    // This used to fade for 200ms and then wait another 200ms before remove(),
    // which added a hard, unconditional 400ms to LCP: the overlay is opaque
    // white and covers the viewport (inset:0, z-index:9999, see the inline
    // script in app/layout.tsx), so nothing underneath can be a Largest
    // Contentful Paint candidate until it is gone. Real-user LCP was 2.92s
    // against a 2.5s target, and 0.4s of that was this animation.
    //
    // pointerEvents is cleared first so the frame where it is still in the DOM
    // cannot swallow a click.
    document.body.classList.remove("loading");
    el.style.pointerEvents = "none";
    el.remove();
  }, [isInitializing]);

  // No React-rendered DOM — the overlay is managed by the inline script
  return null;
}
